// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * A BOM line's remark, edited where it is shown (design doc §7.3, US-B-08).
 *
 * Remarks are written line by line down a 400-row BOM, which through the line dialog is
 * four actions per remark. Here it is click, type, Enter: the cell turns into an input in
 * place, Enter or leaving the cell saves, Escape puts it back untouched.
 *
 * **Enter does not save while an input method is composing.** With a Chinese IME, Enter
 * confirms the candidate word; treating it as "save" would store half a sentence. The
 * check covers `isComposing` and Safari's keyCode 229, which it reports instead.
 *
 * Nothing is sent when the text did not change, so clicking through cells to read them
 * does not write. A failed save keeps the input open with the error under it, rather than
 * snapping back and losing what was typed.
 */

import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorBanner } from "@/components/ErrorBanner";

const REMARK_MAX_LEN = 500;

interface RemarkCellProps {
  remark: string | null;
  /** False where remarks are only shown, not edited. A published BOM still edits them (§7.3). */
  editable: boolean;
  /** Names the line in the accessible name, e.g. its position code. */
  lineLabel: string;
  /** Resolves when saved; rejects to keep the editor open. `null` removes the remark. */
  onSave: (remark: string | null) => Promise<unknown>;
}

export function RemarkCell({ remark, editable, lineLabel, onSave }: RemarkCellProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(remark ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const input = useRef<HTMLInputElement | null>(null);
  // Escape blurs the input on its way out; that blur must not then save.
  const cancelled = useRef(false);

  useEffect(() => {
    if (editing) input.current?.focus();
  }, [editing]);

  if (!editable) {
    return <>{remark ?? <span className="muted">—</span>}</>;
  }

  if (!editing) {
    const open = () => {
      cancelled.current = false;
      setValue(remark ?? "");
      setError(null);
      setEditing(true);
    };
    // The whole cell is the button, filled or empty. Empty, it shows nothing at all —
    // four hundred rows of a prompt or an icon is noise — and a dashed outline appears
    // under the pointer (and on keyboard focus) to say "click here to write one". Its
    // accessible name and tooltip still say what it does.
    return (
      <button
        type="button"
        className="remark-trigger"
        aria-label={t("bom.editRemark", { line: lineLabel })}
        title={t("bom.editRemark", { line: lineLabel })}
        onClick={open}
      >
        {remark}
      </button>
    );
  }

  const save = async () => {
    if (cancelled.current || saving) return;
    const next = value.trim() || null;
    if (next === (remark ?? null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch (failure) {
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (event.nativeEvent.isComposing || event.keyCode === 229) return;
      event.preventDefault();
      void save();
    } else if (event.key === "Escape") {
      // Stop here: a dialog further up must not close because this input was abandoned.
      event.stopPropagation();
      cancelled.current = true;
      setEditing(false);
    }
  };

  return (
    <div className="remark-editor">
      <input
        ref={input}
        aria-label={t("bom.editRemark", { line: lineLabel })}
        value={value}
        maxLength={REMARK_MAX_LEN}
        disabled={saving}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => void save()}
      />
      <ErrorBanner error={error} />
    </div>
  );
}
